import { Box, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { BallTypesTab } from 'components/sections/admin-portal/machines/editor/ball-types.tab';
import { CustomRequestTab } from 'components/sections/admin-portal/machines/editor/custom-request.tab';
import { MachineDetailsTab } from 'components/sections/admin-portal/machines/editor/details.tab';
import { FirmwareConfigTab } from 'components/sections/admin-portal/machines/editor/firmware-config.tab';
import { HardwareConfigTab } from 'components/sections/admin-portal/machines/editor/hardware-config.tab';
import { MachineFeatureFlagsTab } from 'components/sections/admin-portal/machines/editor/machine-feature-flags.tab';
import { RemoteVisionCalibrationTab } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab';
import { MachineSettingsTab } from 'components/sections/admin-portal/machines/editor/settings.tab';
import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { TeamsContext } from 'contexts/admin/teams.context';
import { MachineContext } from 'contexts/machine.context';
import useLatest from 'hooks/useLatestRef';
import { IFullDialog } from 'interfaces/i-dialogs';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import {
  generateModelKeys,
  getMachineActiveModelID,
  getModelKey,
} from 'lib_ts/classes/ms.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DEFAULT_EDITOR_MACHINE, IMachine } from 'lib_ts/interfaces/i-machine';
import { first } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineEditorDialog';

enum TabKey {
  BallTypes = 'BallTypes',
  FirmwareConfig = 'FirmwareConfig',
  HardwareConfig = 'HardwareConfig',
  RemoteVisionCalibration = 'RemoteVisionCalibration',
  CustomRequest = 'CustomRequest',
  Details = 'Details',
  Errors = 'Errors',
  Settings = 'Settings',
  FeatureFlags = 'FeatureFlags',
}

type IProps = {
  isNew?: boolean; // indicates that this is a new machine being created
  onClose?: () => void; // indicates that this component should be shown as a dialog
};

export const MachineEditor = ({ isNew, onClose }: IProps) => {
  const asDialog = !!onClose;
  const [activeTab, setActiveTab] = useState(TabKey.Settings);

  const current = useAuthStore((s) => s.current);

  const teamsCx = useContext(TeamsContext);

  const machine = useMachinesPortalStore((s) => s.machine);
  const machines = useMachinesPortalStore((s) => s.machines);
  const updating = useMachinesPortalStore((s) => s.updating);
  const createMachine = useMachinesPortalStore((s) => s.createMachine);
  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);
  const setShouldRefetchMachines = useMachinesPortalStore(
    (s) => s.setShouldRefetchMachines
  );

  const { openMachineSettingsDialog } = useMachineSettingsDialogState();

  const [formMachine, setFormMachine] = useState<Partial<IMachine>>(
    isNew
      ? {
          ...DEFAULT_EDITOR_MACHINE,
          model_ids: generateModelKeys(),
        }
      : {}
  );

  const { machine: activeMachine, fetchMachine } = useContext(MachineContext);
  const latestActiveMachine = useLatest(activeMachine);

  // keep active machine in sync w/ values from store
  useEffect(() => {
    const updatedMachine = machines.find(
      (m) => m.machineID === latestActiveMachine.current.machineID
    );

    if (!updatedMachine) {
      // shouldn't trigger
      return;
    }

    if (updatedMachine._changed === latestActiveMachine.current._changed) {
      // hasn't changed since last fetch
      return;
    }

    // trigger a fetch machine to ensure the local copy of the active machine matches whatever changed
    fetchMachine();
  }, [machines, fetchMachine]);

  // keep formMachine in sync w/ values from store
  useEffect(() => {
    if (isNew) {
      return;
    }

    const refMachine =
      machines.find((m) => m.machineID === machine?.machineID) ??
      DEFAULT_EDITOR_MACHINE;

    setFormMachine((prev) => ({
      ...refMachine,
      ball_type_options: ArrayHelper.getIntersection(
        refMachine.ball_type_options ?? [BallType.MLB],
        Object.values(BallType)
      ),
      ball_type: refMachine.ball_type ?? BallType.MLB,
      model_ids: {
        ...generateModelKeys(refMachine.model_ids),
        [getModelKey(refMachine)]: getMachineActiveModelID(refMachine) ?? '',
      },
      // TODO: Figure out why we need to keep these things in sync
      // leave what we're actively editing in place
      ...prev,
    }));
  }, [machine?.machineID, machines, isNew]);

  const updateMetadata = (model: Partial<IMachine>) => {
    setFormMachine((prev) => {
      const updated = { ...prev, ...model };
      return updated;
    });
  };

  const conflictedMachineID = useMemo(() => {
    if (!formMachine) {
      return false;
    }

    return (
      machines.findIndex(
        (m) =>
          m.machineID === formMachine.machineID && m._id !== formMachine._id
      ) !== -1
    );
  }, [machines, formMachine]);

  const isSuperAdmin = current.role === UserRole.admin;

  const errors = useMemo(() => {
    const errors = MachineHelper.getErrors(formMachine, !isSuperAdmin);
    if (conflictedMachineID) {
      errors.push('MachineID is already in use');
    }
    return errors;
  }, [formMachine, isSuperAdmin, conflictedMachineID]);

  const onSave = async () => {
    if (errors.length > 0) {
      NotifyHelper.warning({
        message_md: [
          'Please correct the following errors before trying again:',
          errors.map((t) => ` - ${t}`).join('\n'),
        ].join('\n\n'),
        delay_ms: 0,
      });
      return;
    }

    if (isNew) {
      try {
        const result = await createMachine(formMachine);

        if (!result) {
          NotifyHelper.error({
            message_md: 'Failed to create machine, please try again.',
          });

          return;
        }

        NotifyHelper.success({
          message_md: `Machine ${formMachine.machineID} created!`,
        });

        if (asDialog) {
          onClose?.();
          openMachineSettingsDialog(result);
        }

        return;
      } catch (error) {
        NotifyHelper.error({
          message_md: `Failed to create machine: ${error}`,
        });
      }
      return;
    }

    try {
      // turn off Deception Mode if the feature flag is disabled
      if (
        !formMachine.feature_flags?.jitter_mode &&
        formMachine.enable_jitter === true
      ) {
        formMachine.enable_jitter = false;
      }

      const result = first(await updateMachines([formMachine]));

      if (result) {
        NotifyHelper.success({
          message_md: `Machine ${formMachine.machineID} updated!`,
        });
        setFormMachine(result);
        setShouldRefetchMachines(true);
      }
    } catch (error) {
      NotifyHelper.error({
        message_md: `Failed to update machine: ${error}`,
      });
    }
    return;
  };

  const CREATE_CALLOUT = <CommonCallout text="Create the machine first!" />;

  const tabs = (
    <CommonTabs
      value={activeTab}
      className={RADIX.VFLEX.SCROLLABLE}
      contentClassName="height-100p"
      onValueChange={(value) => setActiveTab(value as TabKey)}
      tabs={[
        {
          value: TabKey.Settings,
          label: 'Settings',
          disabled: updating,
          content: (
            <MachineSettingsTab
              machine={formMachine}
              updateMetadata={updateMetadata}
              conflictedMachineID={conflictedMachineID}
            />
          ),
        },
        {
          value: TabKey.Details,
          label: 'Details',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          disabled: updating || teamsCx.loading,
          content: (
            <MachineDetailsTab
              machine={formMachine}
              updateMetadata={updateMetadata}
            />
          ),
        },
        {
          value: TabKey.BallTypes,
          label: 'Ball Types',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          disabled: updating,
          content: (
            <BallTypesTab
              onSave={onSave}
              machine={formMachine}
              updateMetadata={updateMetadata}
              isNew={isNew}
            />
          ),
        },
        {
          value: TabKey.FirmwareConfig,
          label: 'FW Config',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          disabled: updating || !formMachine._id,
          content:
            !isNew && formMachine.machineID ? (
              <FirmwareConfigTab machineID={formMachine.machineID} />
            ) : (
              CREATE_CALLOUT
            ),
        },
        {
          value: TabKey.HardwareConfig,
          label: 'HW Config',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          disabled: updating || !formMachine._id,
          content: !isNew ? (
            <HardwareConfigTab machine={formMachine} />
          ) : (
            CREATE_CALLOUT
          ),
        },
        {
          value: TabKey.RemoteVisionCalibration,
          label: 'Remote Vision Calibration',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin || !formMachine.enable_trajekt_vision,
          disabled: updating || !formMachine._id,
          content:
            !isNew && formMachine.machineID ? (
              <RemoteVisionCalibrationTab />
            ) : (
              CREATE_CALLOUT
            ),
        },
        {
          value: TabKey.FeatureFlags,
          label: 'Feature Flags',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          content: (
            <MachineFeatureFlagsTab
              machine={formMachine}
              updateMetadata={updateMetadata}
            />
          ),
        },
        {
          value: TabKey.CustomRequest,
          label: 'Custom Request',
          labelIcon: <SuperAdminIcon />,
          invisible: !isSuperAdmin,
          disabled: updating || !formMachine._id,
          content:
            !isNew && formMachine.machineID ? (
              <CustomRequestTab machineID={formMachine.machineID} />
            ) : (
              CREATE_CALLOUT
            ),
        },
        {
          value: TabKey.Errors,
          label: 'Errors',
          color: RADIX.COLOR.DANGER,
          invisible: errors.length === 0,
          content: (
            <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
              <Heading size={RADIX.HEADING.SIZE.SM}>Errors</Heading>
              <Box>
                <ul>
                  {errors.map((e, i) => (
                    <li key={`machine-error-${i}`}>{e}</li>
                  ))}
                </ul>
              </Box>
            </Flex>
          ),
        },
      ]}
      scrollOptions={{ background: '--gray-2' }}
      // gap="0"
      gap="2"
      scroll
    />
  );

  // BallTypes implements onSave via its own Save button as it needs to handle disabled/loading state awhile it fetches model options
  const showSaveButton = [
    TabKey.Settings,
    TabKey.Details,
    TabKey.FeatureFlags,
  ].includes(activeTab);
  const saveButtonProps = {
    label: isNew ? 'Create' : 'Save Changes',
    color: isNew ? RADIX.COLOR.SUCCESS : RADIX.COLOR.ACCENT,
    loading: updating,
    variant: RADIX.BUTTON.VARIANT.PRIMARY,
    invisible: !showSaveButton,
    onClick: onSave,
  };

  if (asDialog) {
    const dialogProps: IFullDialog = {
      identifier: COMPONENT_NAME,
      title: isNew ? 'Create Machine' : 'Update Machine',
      width: RADIX.DIALOG.WIDTH.LG,
      loading: updating,
      content: tabs,
      buttons: [saveButtonProps],
      onClose: () => onClose?.(),
    };

    return (
      <ErrorBoundary identifier={COMPONENT_NAME}>
        <CommonDialog {...dialogProps} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex direction="column" flexGrow="1" overflow="hidden">
        <Flex flexGrow="1" direction="column" overflow="hidden">
          {tabs}
        </Flex>
        {showSaveButton && (
          <Flex gap={RADIX.FLEX.GAP.SM} justify="end" mt="5">
            <DialogButton
              label={saveButtonProps.label}
              color={saveButtonProps.color}
              loading={saveButtonProps.loading}
              onClick={saveButtonProps.onClick}
              variant={saveButtonProps.variant}
            />
          </Flex>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
